import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Sparkles, DollarSign, Network, FlaskConical, Tags, Clock, Layers, MessageSquare, Eye, Plug } from "lucide-react";
import { useResearchCapability } from "@/hooks/useResearchCapability";

const tiles = [
  { to: "/partner/research/items", label: "Research items", desc: "Browse, draft and review notes.", icon: FileText, cap: "read" as const },
  { to: "/partner/research/chat", label: "Research chat", desc: "Ask questions across the corpus with citations.", icon: MessageSquare, cap: "read" as const },
  { to: "/partner/research/capsules", label: "Meeting capsules", desc: "Review auto-merged meetings and their synthesised output.", icon: Layers, cap: "read" as const },
  { to: "/partner/research/watch-rules", label: "Watch rules", desc: "Configure ingest rules across Gmail, Drive, Zoom, FRED and more.", icon: Eye, cap: "manage" as const },
  { to: "/partner/research/connectors", label: "Connectors", desc: "Connect Google, Asana, Zoom, Otter and Notion accounts that power ingest.", icon: Plug, cap: "manage" as const },
  { to: "/partner/research/prompts", label: "Prompt templates", desc: "Manage AI prompt actions, models and costs.", icon: Sparkles, cap: "manage" as const },
  { to: "/partner/research/costs", label: "Cost caps", desc: "Monthly spend, caps and breach alerts.", icon: DollarSign, cap: "manage" as const },
  { to: "/partner/research/kg", label: "Knowledge graph", desc: "Browse nodes and edges seeded from research.", icon: Network, cap: "read" as const },
  { to: "/partner/research/evals", label: "Eval harness", desc: "Tasks, judge models and historical runs.", icon: FlaskConical, cap: "manage" as const },
  { to: "/partner/research/taxonomy", label: "Taxonomy", desc: "Controlled vocabulary across all axes.", icon: Tags, cap: "read" as const },
];

const upcoming = [
  { to: "/partner/research/drafts", label: "AI drafts queue", phase: 3 },
  { to: "/partner/research/fred", label: "FRED indicators", phase: 3 },
  { to: "/partner/research/predictions", label: "Predictions ledger", phase: 4 },
  
  { to: "/partner/research/memory", label: "Memory store", phase: 5 },
  { to: "/partner/research/triage", label: "Inbox triage", phase: 5 },
  { to: "/partner/research/drift", label: "Drift monitor", phase: 6 },
  { to: "/partner/research/publication", label: "Publication pipeline", phase: 7 },
];

export default function ResearchHome() {
  const manage = useResearchCapability("manage");
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((t) => {
          const disabled = t.cap === "manage" && manage.data === false;
          const Icon = t.icon;
          const body = (
            <Card className={disabled ? "opacity-50" : "hover:border-primary transition-colors"}>
              <CardContent className="p-5 space-y-2">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">{t.label}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{t.desc}</p>
                {disabled && <p className="text-xs text-muted-foreground">Requires manage capability.</p>}
              </CardContent>
            </Card>
          );
          return disabled ? <div key={t.to}>{body}</div> : <Link key={t.to} to={t.to}>{body}</Link>;
        })}
      </div>

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Coming soon</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {upcoming.map((u) => (
            <Link key={u.to} to={u.to}>
              <Card className="hover:border-primary transition-colors">
                <CardContent className="p-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{u.label}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">Phase {u.phase}</span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
